import { Injectable } from "@angular/core";

import { BillingHistoryPage } from 'src/pages/billing-history/billing-history';
import { TeamsPage } from 'src/pages/team/teams/teams';
import { UsersPage } from 'src/pages/users/users';
import { ContactInfoPage } from 'src/pages/more/contact-info/contact-info';
import { AccountTypePage } from 'src/pages/more/account-type/account-type';
import { BackupTimesPage } from 'src/pages/more/backup-times/backup-times';
import { AccountNotificationSettingsPage } from 'src/pages/more/account-notification-settings/account-notification-settings';
import { BulkUploadPage } from 'src/pages/more/bulk-upload/bulk-upload';
import { CompliancePage } from 'src/pages/compliance/compliance.page';
import { DevelopersPage } from 'src/pages/more/developers/developers';
import { PaymentMethodsPage } from 'src/pages/more/payment-methods/payment-methods';
import { PaymentSettingsPage } from 'src/pages/more/payment-settings/payment-settings';
import { UseGiftCardPage } from 'src/pages/more/use-gift-card/use-gift-card';
import { MyPricesPage } from 'src/pages/more/my-prices/my-prices';
import { SubscriptionsPage } from 'src/pages/subscription/subscription';
import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { ToDosListSettingsPage } from 'src/pages/to-dos/list-settings/list-settings';
import { WorkflowsPage } from 'src/pages/workflows/workflows';
import { WorkflowHistoryPage } from 'src/pages/workflows/workflow-history/workflow-history';
import { JobRequestWorkflows } from 'src/pages/job-request-workflows/job-request-workflows';
import { AutomaticBookingPage } from 'src/pages/more/automatic-booking/automatic-booking';
import { RentalModesService } from 'src/shared/services/rental-modes.service';

/**
 * One row of the global search "PAGES" list. The gate flags mirror
 * DesktopNavComponent — a page is only searchable where the nav can reach it.
 */
export interface SearchablePage {
  title: string;
  link: string;
  image: string;
  searchTerms?: string[];
  isRightSidePanel?: boolean;
  component?: any;
  /** Rental accounts only — residential and office accounts have no such section. */
  rentalOnly?: boolean;
  /** Hidden from rental accounts — cleaning-price features that don't fit the rental product. */
  nonRentalOnly?: boolean;
  /** Also gated by the Account Settings long-term-rentals toggle (Leasing, Deposits, eSignature, …). */
  longTermOnly?: boolean;
}

@Injectable()
export class HeaderProvider {

  constructor(private rentalModes: RentalModesService) {}

  showConciergePage: any;
  showNotificationCenter: any;

  helpArticlesItems = [
    {
      title: 'Getting Started',
      link: 'https://www.tidy.com/docs/pm/getting-started',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Dashboard',
      link: 'https://help.tidy.com/dashboard',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'TIDY Concierge',
      link: 'https://help.tidy.com/tidy-concierge',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'TIDY AI',
      link: 'https://help.tidy.com/tidy-ai',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Groups & Users',
      link: 'https://help.tidy.com/teams-and-users',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Properties/Addresses',
      link: 'https://help.tidy.com/propertiesaddresses',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Schedule',
      link: 'https://help.tidy.com/schedule',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Jobs',
      link: 'https://help.tidy.com/jobs',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Workflows',
      link: 'https://help.tidy.com/workflows',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Account Settings',
      link: 'https://help.tidy.com/account-settings',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Messages',
      link: 'https://help.tidy.com/messages',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Notifications',
      link: 'https://help.tidy.com/notifications',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Plans',
      link: 'https://help.tidy.com/plans',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Access Control / Smartlocks',
      link: 'https://help.tidy.com/access-control-smartlocks',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Contacts',
      link: 'https://help.tidy.com/my-pros',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Checklists',
      searchTerms: [
        'checklist',
        'checklists',
        'to-dos',
        'to-do'
      ],
      link: 'https://help.tidy.com/checklists',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Integrations',
      link: 'https://help.tidy.com/integrations',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Auto-Schedule',
      link: 'https://help.tidy.com/automatic-booking',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Billing, Pricing, & Payments',
      link: 'https://help.tidy.com/billing-pricing-and-payments',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Compliance, Ratings, Safety, Insurance, & Protection',
      link: 'https://help.tidy.com/compliance-ratings-safety-insurance-and-protection',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Property Maps',
      link: 'https://help.tidy.com/property-maps',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Maintenance',
      link: 'https://help.tidy.com/maintenance',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Inventory Tracking',
      link: 'https://help.tidy.com/inventory-tracking',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Inspections',
      link: 'https://help.tidy.com/inspections',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Smart Thermostats',
      link: 'https://help.tidy.com/smart-thermostats',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Tasks',
      link: 'https://help.tidy.com/tasks',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Automatic Translation',
      link: 'https://help.tidy.com/automatic-translation',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Other Questions',
      link: 'https://help.tidy.com/other-questions',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Reports',
      link: 'https://help.tidy.com/reports',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Accounting',
      link: 'https://help.tidy.com/accounting',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Developers',
      link: 'https://help.tidy.com/developers',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Best Practices',
      link: 'https://help.tidy.com/best-practices',
      image: 'assets/img/help-fill.svg'
    },
    {
      title: 'Changelog',
      link: 'https://www.tidy.com/blog?tag=changelog',
      image: 'assets/img/help-fill.svg'
    },
  ];

  sectionsItems: SearchablePage[] = SEARCHABLE_PAGES;

  /**
   * The PAGES list for global search, filtered to what this account can
   * actually reach. Mirrors the desktop nav's gates so search never offers a
   * section the nav doesn't show: `account_type === 'rental'` and the Account
   * Settings long-term-rentals toggle.
   */
  getSearchablePages(): SearchablePage[] {
    const isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    const hasLongTermRentals = this.rentalModes.modes.longTerm;
    return this.sectionsItems.filter((page) => {
      if (page.rentalOnly && !isRentalClient) { return false; }
      if (page.nonRentalOnly && isRentalClient) { return false; }
      if (page.longTermOnly && !hasLongTermRentals) { return false; }
      return true;
    });
  }
}

/**
 * Every page reachable from global search. This list and the desktop nav
 * (`desktop-nav-items.ts` + `DesktopNavComponent`) describe the same product
 * surface by hand, so they drift — Inquiries, Direct Booking, eSignature,
 * Leasing and the long-term Finance tabs all shipped in the nav without ever
 * becoming searchable. Add a row here whenever you add a nav destination, and
 * flag it with the same gate the nav uses.
 */
export const SEARCHABLE_PAGES: SearchablePage[] = [
    {
      title: 'Dashboard',
      link: '/overview',
      searchTerms: [
        'dashboard',
        'overview',
        'operations',
        'properties overview'
      ],
      // Rental accounts reach this page as Properties, below.
      nonRentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Chat',
      link: '/chat',
      searchTerms: [
        'chat',
        'ai',
        'tidy ai',
        'assistant',
        'ask tidy',
        'copilot'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Schedule',
      link: '/schedule',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Bills',
      link: '/bills',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Finance',
      link: '/finance',
      searchTerms: [
        'finance',
        'accounting',
        'money',
        'financials',
        'bookkeeping',
        'ledger'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Transactions',
      link: '/finance?tab=transactions',
      searchTerms: [
        'transaction',
        'transactions',
        'payments',
        'ledger',
        'charges',
        'payouts'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Taxes',
      link: '/finance?tab=taxes',
      searchTerms: [
        'tax',
        'taxes',
        '1099',
        'property tax',
        'tax form',
        'irs',
        'w9'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Deposits',
      link: '/finance?tab=deposits',
      searchTerms: [
        'deposit',
        'deposits',
        'security deposit',
        'damage deposit',
        'holding deposit',
        'held funds'
      ],
      rentalOnly: true,
      longTermOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Rent Collection',
      link: '/rent-collection',
      searchTerms: [
        'rent',
        'rent collection',
        'collect rent',
        'rent payments',
        'tenant payments',
        'rent roll'
      ],
      rentalOnly: true,
      longTermOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Leases',
      link: '/finance?tab=leases',
      searchTerms: [
        'lease',
        'leases',
        'lease agreement',
        'rental agreement',
        'tenancy',
        'tenant lease'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Monthly P&L',
      link: '/finance?tab=monthly',
      searchTerms: [
        'p&l',
        'pnl',
        'profit and loss',
        'monthly',
        'monthly p&l',
        'income statement',
        'profit'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Restocking',
      link: '/restocking',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Agents',
      link: '/agent',
      searchTerms: [
        'agent',
        'agents',
        'automations',
        'automation',
        'ai agent',
        'ai',
        'bots',
        'autopilot'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Checklists',
      searchTerms: [
        'checklist',
        'checklists',
        'to-dos',
        'to-do'
      ],
      link: '/checklists',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Contacts',
      link: '/contacts',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Pros',
      link: '/contacts?tab=pros',
      searchTerms: [
        'pros',
        'vendors',
        'contractors',
        'cleaners',
        'service providers',
        'staff'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Guests/Tenants',
      link: '/contacts?tab=guests',
      searchTerms: [
        'guests',
        'guest',
        'tenants',
        'tenant',
        'guests/tenants',
        'renters',
        'occupants',
        'stays'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'More',
      link: '/more',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Properties',
      link: '/properties',
      searchTerms: [
        'properties',
        'dashboard',
        'operations'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Upgrades',
      link: '/upgrades',
      searchTerms: [
        'upgrades',
        'property upgrades',
        'improvements',
        'roi',
        'impact',
        'address upgrade'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Reservations',
      link: '/more/reservations',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Job History',
      link: '/past-jobs',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Billing History',
      link: '/billing-history',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: BillingHistoryPage
    },
    {
      title: 'Reports',
      link: '/reports',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Integrations',
      link: '/integration/all',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Audit Logs',
      link: '/audit-logs',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Marketing',
      link: '/revenue',
      searchTerms: [
        'marketing',
        'promotions',
        'promote',
        'growth',
        'sales',
        'advertising'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Upsells',
      link: '/revenue?tab=upsells',
      searchTerms: [
        'upsell',
        'upsells',
        'add-ons',
        'addons',
        'extras',
        'cross-sell',
        'ancillary'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Listings',
      link: '/listings',
      searchTerms: [
        'listings',
        'listing',
        'channels',
        'channel manager',
        'ota',
        'airbnb',
        'vrbo',
        'booking.com',
        'distribution'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Reviews',
      link: '/revenue?tab=reviews',
      searchTerms: [
        'review',
        'reviews',
        'ratings',
        'guest reviews',
        'feedback',
        'star rating',
        'reputation'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Leasing',
      link: '/revenue?tab=leasing',
      searchTerms: [
        'leasing',
        'lease listing',
        'long term',
        'long-term',
        'rental listing',
        'zillow',
        'tenant marketing'
      ],
      rentalOnly: true,
      longTermOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Inquiries',
      link: '/inquiries',
      searchTerms: [
        'inquiry',
        'inquiries',
        'booking request',
        'booking requests',
        'requests',
        'pre-approval',
        'preapproval',
        'approve booking'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Direct Booking',
      link: '/direct-booking',
      searchTerms: [
        'direct booking',
        'direct bookings',
        'book direct',
        'booking site',
        'booking website',
        'own website'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Guidebook',
      link: '/guidebook',
      searchTerms: [
        'guidebook',
        'guide book',
        'guest guide',
        'welcome guide',
        'house manual',
        'recommendations'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'eSignature',
      link: '/e-sign',
      searchTerms: [
        'esignature',
        'e-signature',
        'esign',
        'e-sign',
        'signature',
        'signing',
        'sign documents',
        'contracts'
      ],
      rentalOnly: true,
      longTermOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Wi-Fi Marketing',
      link: '/revenue?tab=wifi',
      searchTerms: [
        'wifi',
        'wi-fi',
        'wifi marketing',
        'internet',
        'captive portal',
        'guest wifi',
        'hotspot'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Happiness',
      link: '/revenue?tab=happiness',
      searchTerms: [
        'happiness',
        '11-star quality',
        'eleven star',
        'quality',
        'offerings',
        'guest experience',
        'comp',
        'upsell offering'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Messages',
      link: '/messages',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Maintenance',
      link: '/maintenance',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Groups',
      link: '/groups',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: TeamsPage
    },
    {
      title: 'Users',
      link: '/users',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: UsersPage
    },
    {
      title: 'Contact Info',
      link: '/contact-info',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: ContactInfoPage
    },
    {
      title: 'Account Type',
      link: '/account-type',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: AccountTypePage
    },
    {
      title: 'Default Backup Times',
      link: '/backup-times',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: BackupTimesPage
    },
    {
      title: 'Account Notifications',
      link: '/account-notifications',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: AccountNotificationSettingsPage
    },
    {
      title: 'Bulk Upload',
      link: '/more/bulk-upload',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: BulkUploadPage
    },
    {
      title: 'Compliance',
      link: '/compliance',
      rentalOnly: true,
      image: 'assets/img/shield-white.svg',
      isRightSidePanel: false,
      component: CompliancePage
    },
    {
      title: 'Noise Monitoring',
      link: '/noise-monitoring',
      searchTerms: [
        'noise',
        'noise monitoring',
        'decibel',
        'db',
        'minut',
        'noiseaware',
        'sound',
        'party detection'
      ],
      rentalOnly: true,
      image: 'assets/img/shield-white.svg'
    },
    {
      title: 'Insurance',
      link: '/insurance',
      searchTerms: [
        'insurance',
        'liability',
        'coverage',
        'damage protection',
        'policy',
        'claim',
        'proper insurance'
      ],
      rentalOnly: true,
      image: 'assets/img/shield-white.svg'
    },
    {
      title: 'Developers',
      link: '/developers',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: DevelopersPage
    },
    {
      title: 'Payment Methods',
      link: '/payment-methods',
      searchTerms: [
        'payment',
        'credit card',
        'billing'
      ],
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: PaymentMethodsPage
    },
    {
      title: 'Payment Settings',
      link: '/payment-settings',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: PaymentSettingsPage
    },
    {
      title: 'Gift Card',
      link: '/use-gift-card',
      image: 'assets/img/globe-white.svg',
      nonRentalOnly: true,
      isRightSidePanel: true,
      component: UseGiftCardPage
    },
    {
      title: 'My Prices',
      link: '/my-prices',
      searchTerms: [
        'cost',
        'pricing',
      ],
      image: 'assets/img/globe-white.svg',
      nonRentalOnly: true,
      isRightSidePanel: true,
      component: MyPricesPage
    },
    {
      title: 'Subscription',
      link: '/my-prices',
      rentalOnly: true,
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: SubscriptionsPage
    },
    {
      title: 'Book Job(s)',
      searchTerms: [
        'schedule job',
        'book job'
      ],
      link: '/select-category',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: SelectCategoryPage
    },
    {
      // The routes were renamed to `*-maintenance`; '/recurring-tasks' and
      // '/archived-tasks' navigated nowhere.
      title: 'Recurring Tasks',
      link: '/recurring-maintenance',
      searchTerms: [
        'recurring tasks',
        'recurring maintenance',
        'recurring',
        'scheduled maintenance'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Archived Tasks',
      link: '/archived-maintenance',
      searchTerms: [
        'archived tasks',
        'archived maintenance',
        'archived',
        'closed tasks'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Default Checklists',
      link: '/list-settings',
      searchTerms: [
        'checklist',
        'checklists',
        'to-dos',
        'to-do'
      ],
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: ToDosListSettingsPage
    },
    {
      title: 'Pro Settings',
      link: '/pro-settings',
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Workflows',
      link: '/automations/workflows',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: WorkflowsPage
    },
    {
      title: 'Workflow Run History',
      link: '/automations/workflow-history',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: WorkflowHistoryPage
    },
    {
      title: 'Pro Search Agent',
      link: '/job-request-workflows',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: JobRequestWorkflows
    },
    {
      title: 'Auto-Schedule',
      link: '/automatic-booking',
      image: 'assets/img/globe-white.svg',
      isRightSidePanel: true,
      component: AutomaticBookingPage
    },
    {
      title: 'Optimizer',
      link: '/optimizer',
      searchTerms: [
        'optimizer',
        'booking optimizer',
        'booking settings',
        'optimization settings',
        'occupancy',
        'revenue optimizer',
        'set prices',
        'set price',
        'prices',
        'pricing',
        'price range',
        'rates',
        'nightly rate',
        'discounts',
        'discount',
        'minimum stay',
        'min nights',
        'max nights',
        'stay duration',
        'pet fee',
        'pet friendly',
        'check-in time',
        'check-out time',
        'cancellation policy',
        'instant book',
        'listing optimization',
        'channel optimization',
        'seasonal pricing',
        'booking rules'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Assets',
      link: '/assets',
      searchTerms: [
        'assets',
        'asset',
        'appliances',
        'equipment',
        'inventory',
        'items',
        'maintenance items'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Access',
      link: '/access',
      searchTerms: [
        'access',
        'access control',
        'smart locks',
        'smartlock',
        'locks',
        'keys',
        'entry codes',
        'door codes'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Concierge',
      link: '/concierge',
      searchTerms: [
        'concierge',
        'tidy concierge',
        'support',
        'help',
        'assistant',
        'requests'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Overview',
      link: '/overview',
      searchTerms: [
        'overview',
        'income',
        'strategy',
        'earnings',
        'revenue',
        'profit',
        'financials',
        'finance overview'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Automations',
      link: '/automations/summary',
      searchTerms: [
        'automations',
        'automation',
        'automation summary',
        'autopilot'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Account Settings',
      link: '/account-settings',
      searchTerms: [
        'account settings',
        'settings',
        'profile',
        'preferences',
        'account'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Getting Started',
      link: '/getting-started',
      searchTerms: [
        'getting started',
        'get started',
        'setup',
        'onboarding',
        'tutorial'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Notification Center',
      link: '/notification-center',
      searchTerms: [
        'notifications',
        'notification center',
        'alerts',
        'inbox',
        'notification settings'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Connected Bank Accounts',
      link: '/manage-connections',
      searchTerms: [
        'bank',
        'bank account',
        'bank accounts',
        'connected accounts',
        'connections',
        'plaid',
        'banking',
        'ach'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'API Keys',
      link: '/api-keys',
      searchTerms: [
        'api',
        'api keys',
        'api key',
        'developer',
        'tokens',
        'integration'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Webhooks',
      link: '/webhooks',
      searchTerms: [
        'webhook',
        'webhooks',
        'developer',
        'callbacks',
        'api',
        'events'
      ],
      image: 'assets/img/globe-white.svg'
    },
    {
      title: 'Upsell Settings',
      link: '/upsell-settings',
      searchTerms: [
        'upsell settings',
        'upsell configuration',
        'upsells',
        'add-ons'
      ],
      rentalOnly: true,
      image: 'assets/img/globe-white.svg'
    },
];

